import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../../auth/AuthContext";

type IndexPoint = {
  referenceDate: string;
  value: number;
};

type IndexDashboard = {
  ipca: { month: number; ytd: number; rolling12m: number };
  igpm: { month: number; ytd: number; rolling12m: number };
  series: {
    ipca: IndexPoint[];
    igpm: IndexPoint[];
  };
};

const fmtPct = (v: number) => `${(Number(v) * 100).toFixed(2)}%`;

const IndexCard: React.FC<{
  label: string;
  color: string;
  month: number;
  ytd: number;
  rolling12m: number;
}> = ({ label, color, month, ytd, rolling12m }) => (
  <div className="card shadow-sm h-100" style={{ borderTop: `3px solid ${color}` }}>
    <div className="card-body">
      <h6 className="fw-bold mb-3" style={{ color }}>{label}</h6>
      <div className="row g-0 text-center">
        <div className="col-4 border-end">
          <div className="text-muted small">Mensal</div>
          <div className="fw-bold fs-5 mt-1">{fmtPct(month)}</div>
        </div>
        <div className="col-4 border-end">
          <div className="text-muted small">Acum. Ano</div>
          <div className="fw-bold fs-5 mt-1">{fmtPct(ytd)}</div>
        </div>
        <div className="col-4">
          <div className="text-muted small">12 meses</div>
          <div className="fw-bold fs-5 mt-1">{fmtPct(rolling12m)}</div>
        </div>
      </div>
    </div>
  </div>
);

export const IndicesDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [data, setData] = useState<IndexDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get("/dash/indices");
      if (!response.ok) {
        setData(null);
        setError("Falha ao carregar indices.");
        setLoading(false);
        return;
      }
      const payload = (await response.json()) as { data: IndexDashboard };
      setData(payload.data);
      setLoading(false);
    };
    fetchData();
  }, [client]);

  const series = useMemo(() => {
    const ipca = data?.series?.ipca ?? [];
    const igpm = data?.series?.igpm ?? [];
    const byDate = new Map<string, { date: string; IPCA?: number; IGPM?: number }>();
    ipca.forEach((item) => {
      const date = item.referenceDate.slice(0, 7);
      byDate.set(date, { ...(byDate.get(date) ?? { date }), IPCA: Number((Number(item.value) * 100).toFixed(2)) });
    });
    igpm.forEach((item) => {
      const date = item.referenceDate.slice(0, 7);
      byDate.set(date, { ...(byDate.get(date) ?? { date }), IGPM: Number((Number(item.value) * 100).toFixed(2)) });
    });
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data?.series]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Indices Economicos</h1>

      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <IndexCard
            label="IPCA"
            color="#0d6efd"
            month={data?.ipca?.month ?? 0}
            ytd={data?.ipca?.ytd ?? 0}
            rolling12m={data?.ipca?.rolling12m ?? 0}
          />
        </div>
        <div className="col-md-6">
          <IndexCard
            label="IGP-M"
            color="#6610f2"
            month={data?.igpm?.month ?? 0}
            ytd={data?.igpm?.ytd ?? 0}
            rolling12m={data?.igpm?.rolling12m ?? 0}
          />
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h6 className="card-title">Variacao mensal — serie historica (%)</h6>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={45} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Legend />
              <Line type="monotone" dataKey="IPCA" stroke="#0d6efd" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="IGPM" stroke="#6610f2" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
